import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';

import * as ActionCreators from '../../actions/ActionCreators';
import * as ActionTypes from '../../actions/ActionTypes';
import { serialize, handleErrors, isHLSSupported } from '../../utils/ASVPUtils';

import {
    UNSUPPORTED_CSS_CLASS,
    ERROR_CSS_CLASS,
    ERROR_FETCHING_SOURCE_SLATE,
    HLS_MIME_TYPE,
    VIDEO_SOURCE_QUERY_PARAMS,
    VIDEO_SOURCE_URL,
    MEDIA_JSON_SOURCE_URL
} from '../../constants';


const setSourceFromVideoTagEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_READY)
    .filter(() => !options.videoID)
    .filter(() => !store.getState().player.videoID)
    .map(() => player.tech('Html5').currentSrc())
    .filter(src => !!src)
    .do(src => player.src({ 'src': src }))
    .ignoreElements();


// when player is ready and/or video id is set -- fetch the data based on the videoID in the state
// the initial state might have the video id from how it's passed in on player creation (PLAYER_EVENT_READY)
// SET_VIDEO_ID will have set the video id to a new one.
const fetchVideoSourceEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.SET_VIDEO_ID)
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(videoID => Observable.fromPromise(
        fetch(`${VIDEO_SOURCE_URL}${videoID}?${serialize(VIDEO_SOURCE_QUERY_PARAMS)}`) // gets new video data from id
            .then(handleErrors)
            .then(res => res.json())
            .then(response => ActionCreators.receiveVideoSource(response))
            .catch(data => ActionCreators.fetchVideoSourceError(data))
    ));


const fetchMediaSourceEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.SET_VIDEO_ID)
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(videoID => Observable.fromPromise(
        fetch(`${MEDIA_JSON_SOURCE_URL}${videoID}/desktop`) // gets new video data from id
            .then(handleErrors)
            .then(res => res.json())
            .then(response => ActionCreators.receiveMediaSource(response))
            .catch(data => ActionCreators.fetchVideoSourceError(data))
    ));


// sets slate for video source error
const fetchVideoSourceError = (action$, __, { player }) => action$
    .ofType(ActionTypes.FETCH_VIDEO_SOURCE_ERROR)
    .do(data => console.error('fetchVideoSourceError', data))
    .do(() => player.addClass(ERROR_CSS_CLASS))
    .do(() => player.pause())
    .do(() => player.poster(ERROR_FETCHING_SOURCE_SLATE))
    .ignoreElements();


// called when video and media source are finished processing
// because we need both sources for playback
const receiveVideoPlayerDataEpic = (action$, store, { }) => action$
    .ofType(ActionTypes.SET_VIDEO_ID)
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(() => Observable.combineLatest(
        action$.ofType(ActionTypes.RECEIVE_VIDEO_SOURCE),
        action$.ofType(ActionTypes.RECEIVE_MEDIA_SOURCE),
        action$.ofType(ActionTypes.RECEIVE_CDN_TOKEN_SOURCE)
    ).take(1))
    .map(() => ActionCreators.receiveVideoPlayerData()); // get video source state from store


// sets the player src and poster from the fetched data sources
const setVideoSourceEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.RECEIVE_VIDEO_PLAYER_DATA)
    .map(() => store.getState()) // get video source state from store
    .filter(state => !!state.videoSource.contentURL)
    .do(state => player.poster(state.videoSource.poster)) // set poster
    .do(state => player.src({ 'type': HLS_MIME_TYPE, 'src': `${state.videoSource.contentURL}?hdnts=${state.cdnToken.token}` }))
    .do(() => (player.currentType() === HLS_MIME_TYPE && !isHLSSupported()) ? player.addClass(UNSUPPORTED_CSS_CLASS) : player.removeClass(UNSUPPORTED_CSS_CLASS)) // set/unset unsupported css class if can't play media
    .map(() => ActionCreators.setVideoPlayerData());



const resetVideoSourceEpic = (action$, store, { player }) => Observable.merge(
        action$.ofType(ActionTypes.ADS_PREROLL_END),
        action$.ofType(ActionTypes.ADS_MIDROLL_END),
        action$.ofType(ActionTypes.RECONNECT_STREAM),
    )
    .map(() => store.getState()) // get video source state from store
    .filter(state => !!state.videoSource.contentURL)
    .do(state => player.src({ 'type': HLS_MIME_TYPE, 'src': `${state.videoSource.contentURL}?hdnts=${state.cdnToken.token}` }))
    .ignoreElements();


export default combineEpics(
    fetchMediaSourceEpic,
    fetchVideoSourceEpic,
    fetchVideoSourceError,
    receiveVideoPlayerDataEpic,
    resetVideoSourceEpic,
    setSourceFromVideoTagEpic,
    setVideoSourceEpic,
);



// WEBPACK FOOTER //
// ./src/player/videoSource/epics.js